/**
 * PRP-IMPORT-PRICENOTE-1 item 6: Settings tab for the user's price note list. Every change
 * saves at once; the server's reply is the list on screen.
 */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import { PriceNotesAPI } from '../../../lib/priceNotesApi';
import { IconTrash } from '../icons';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Skeleton } from '../ui/skeleton';
import { FieldLabel, Section } from './SettingsFrame';

export default function PriceNotesPanel() {
  const [notes, setNotes] = useState<string[] | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    PriceNotesAPI.list()
      .then((r) => {
        if (!cancelled) setNotes(r.notes ?? []);
      })
      .catch(() => {
        if (!cancelled) {
          setNotes([]);
          setError('Could not load your price notes.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const change = async (call: Promise<{ notes: string[] }>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      setNotes((await call).notes);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The change could not be saved. Try again.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (await change(PriceNotesAPI.add(text))) setText('');
  };

  return (
    <Section
      title="Price notes"
      sub="Text a line can carry in place of a price. A line marked with one stays at zero in Xactimate."
    >
      <div data-testid="price-notes-section">
        <form className="flex items-end gap-2 max-w-[420px]" onSubmit={(e) => void add(e)}>
          <div className="flex-1 min-w-0">
            <FieldLabel htmlFor="price-note-new">Add one</FieldLabel>
            <Input
              id="price-note-new"
              value={text}
              maxLength={40}
              onChange={(e) => setText(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={busy || !text.trim()}>
            Add
          </Button>
        </form>
        {error && (
          <p role="alert" className="mt-2 mb-0 text-[12.5px] text-[var(--bad)]">
            {error}
          </p>
        )}
        {notes === null ? (
          <Skeleton className="mt-4 h-24 w-full" />
        ) : (
          <ul className="mt-4 mb-0 p-0 list-none grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-[6px]">
            {notes.map((note) => (
              <li
                key={note}
                className="flex items-center justify-between gap-2 min-h-[34px] pl-3 pr-1 rounded-[var(--r-sm)] border border-[var(--bd-subtle)] text-[12.5px] text-[var(--tx-hi)]"
              >
                <span className="min-w-0 break-words">{note}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-7 px-0 justify-center shrink-0"
                  aria-label={`Remove ${note}`}
                  title="Remove"
                  disabled={busy}
                  onClick={() => void change(PriceNotesAPI.remove(note))}
                >
                  <IconTrash className="!w-[14px] !h-[14px]" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Section>
  );
}
